/**
 * 이상한 방 - 봉투 열기(결정론 · 피티 · 이력)
 *
 * 근거 문서: 로드맵 확정안 3.2절(봉투 - 결정론) · 3.3절(연출 - 바늘의 최종 위치는 rng가 뽑은 실수 그 값)
 *           4절 8·9번(하드 피티 · 정직한 바늘) · `이상한방-상태모델-v0.1.md` 11.4절(예측의 ROI는 음수)
 *           난수 계약은 `core/rng.ts` 머리말
 *
 * ## 시드 파생 - 예측할 수 있지만 얻는 것이 없다
 *
 * `createRng(`${accountSeed}:envelope:${seq}`)`. 계정 시드는 `app/accountSeed.ts`가 첫 실행에 한 번 만들어
 * 저장하고(v3), `seq`는 뽑을 때만 오른다. 플레이어가 저장소를 열어 다음 결과를 미리 볼 수는 있다 -
 * 그러나 **안 뽑으면 그 결과가 그대로 다음 것**이라 기다려서 바꿀 수 있는 것이 없고, 랭킹도 결제도 없어
 * 11.4절대로 ROI가 음수다. 서버 계정이 생기면 시드가 서버로 가고 이 함수는 그대로다.
 *
 * ## 난수 소비량은 언제나 둘이다
 *
 * 등급 롤 하나 + 항목 롤 하나. **피티가 등급을 강제해도 둘 다 소비한다.** `rng.ts`의 `chance(0)`이
 * 난수를 소비하는 이유와 같다 - 소비량이 조건에 따라 흔들리면 "피티 임계를 9로 바꿨더니 다른 회차의
 * 결과가 전부 달라졌다"가 되고, 그 순간 `?pull=seq`로 회차를 재현하는 도구가 거짓말을 한다.
 *
 * ## 바늘은 거짓말하지 않는다
 *
 * `DrawResult.gradeRoll`은 rng가 뽑은 실수 그 값이고, 연출의 바늘은 그 위치에서 멎는다.
 * SR 경계 옆에 멎는 근접 실패는 연출이 아니라 **사실**이다(로드맵 6절이 "근접 실패 색 깜빡"을
 * 확률표를 몸짓으로 속이는 것이라 기각했다). 피티가 강제한 회차는 `forcedBy`로 따로 표시하므로
 * 연출은 "바늘은 N에 멎었지만 피티가 SR으로 올렸다"를 그대로 그릴 수 있다.
 *
 * ## 이력은 20줄
 *
 * `magazines.ts`의 지급 이력과 같은 이유로 잘라 둔다 - `localStorage`는 용량을 넘기면 `setItem`이 던지고,
 * 그 순간 **진행 기록까지 통째로 저장되지 않는다.** 봉투 이력 때문에 클리어 기록이 날아가는 것은
 * 어떤 연출 이득으로도 정당화되지 않는다.
 */

import { createRng } from '../../core/rng'
import {
  ENVELOPE_TABLE,
  GRADES,
  WEIGHT_TOTAL,
  gradeRank,
  isGrade,
  parseReward,
  type EnvelopeTable,
  type Grade,
  type PoolEntry,
  type Reward,
} from './envelopeTable'

/* --- 타입 ------------------------------------------------------------------- */

/** 피티 카운터 둘. 저장되고 화면에 게이지로 보인다("SR까지 몇 장"). */
export type Pity = Readonly<{
  /** 마지막 SR **이상** 뒤로 열어 본 장수. */
  sinceRare: number
  /** 마지막 SSR 뒤로 열어 본 장수. */
  sinceLegend: number
}>

/** 피티가 등급을 올렸는가. `null`이면 바늘이 멎은 그 등급이 결과다. */
export type PityForce = 'rare' | 'legend' | null

/** 봉투 하나를 연 결과. 연출이 그리는 재료 전부가 여기 있고, 연출은 이것 밖의 난수를 만들지 않는다. */
export type DrawResult = Readonly<{
  /** 이 계정에서 몇 번째 봉투인가. 1부터. */
  seq: number
  /** 최종 등급(피티 반영). */
  grade: Grade
  reward: Reward
  /** 바늘이 멎은 등급(피티 반영 전). `forcedBy`가 `null`이면 `grade`와 같다. */
  rolledGrade: Grade
  /** 등급 롤의 실수 그 값 [0, 1). 바늘의 최종 위치다. */
  gradeRoll: number
  /** 항목 롤의 실수 그 값 [0, 1). 카드가 몇 번째 항목인지의 근거다. */
  itemRoll: number
  forcedBy: PityForce
  /** 이 봉투를 연 뒤의 피티. 다음 봉투가 이 값을 받는다. */
  pityAfter: Pity
}>

/** 계정의 봉투 상태. 진행 저장(v3)에 통째로 얹힌다. */
export type EnvelopeState = Readonly<{
  /** 지금까지 연 장수. 다음 봉투의 `seq`는 이 값 + 1이다. */
  seq: number
  sinceRare: number
  sinceLegend: number
  /** 최근 결과. 오래된 것부터 잘려 나간다(`ENVELOPE_LOG_LIMIT`). */
  log: readonly DrawResult[]
}>

/** 바늘 띠 한 칸. 연출이 "N 60 / R 30 / SR 9 / SSR 1의 실제 폭"으로 띠를 그리는 재료다. */
export type GradeBand = Readonly<{ grade: Grade; from: number; to: number }>

/* --- 상수 ------------------------------------------------------------------- */

/** 남겨 두는 봉투 이력 수. 근거는 머리말. */
export const ENVELOPE_LOG_LIMIT = 20

/** 첫 봉투의 번호. `magazines.ts`의 지급 이력과 같이 1부터다. */
const FIRST_SEQ = 1

/** 새 계정의 피티. 첫 봉투(`seq === 1`)는 언제나 여기서 출발한다. */
export const FRESH_PITY: Pity = { sinceRare: 0, sinceLegend: 0 }

/* --- 시드 -------------------------------------------------------------------- */

/** 봉투 하나의 시드. 이 문자열 형식이 곧 재현 규약이다(`?pull=seq`). */
export function envelopeSeed(accountSeed: string, seq: number): string {
  return `${accountSeed}:envelope:${String(seq)}`
}

/* --- 등급 ------------------------------------------------------------------- */

/**
 * 가중치를 [0, 1) 위의 띠로 편다. 등급 순서(낮은 것부터)대로 이어 붙이므로 SSR이 오른쪽 끝이다 -
 * 바늘이 오른쪽으로 갈수록 좋은 것이라는 연출의 상식과 같다.
 */
export function gradeBands(table: EnvelopeTable = ENVELOPE_TABLE): readonly GradeBand[] {
  const bands: GradeBand[] = []
  let cumulative = 0
  for (const grade of GRADES) {
    const from = cumulative / WEIGHT_TOTAL
    cumulative += table.weights[grade]
    bands.push({ grade, from, to: cumulative / WEIGHT_TOTAL })
  }
  return bands
}

/**
 * 바늘 값 → 등급. `roll`은 [0, 1).
 *
 * 정수 가중치와 비교한다(`roll × 1000 < 누적`). 부동소수점 띠 경계와 비교하면 마지막 띠에서 반올림 한 번에
 * 인덱스가 넘칠 수 있다(`rng.ts`의 `int`가 같은 이유로 상한을 한 번 더 자른다). 마지막 등급이 폴백이다.
 */
export function gradeForRoll(roll: number, table: EnvelopeTable = ENVELOPE_TABLE): Grade {
  const scaled = roll * WEIGHT_TOTAL
  let cumulative = 0
  for (const grade of GRADES) {
    cumulative += table.weights[grade]
    if (scaled < cumulative) return grade
  }
  return GRADES[GRADES.length - 1] ?? 'legend'
}

/**
 * 피티를 적용한다. SSR 피티가 먼저다 - 60장 안에 SSR이 없었다면 그 사람은 SR 피티도 여러 번 겪었고,
 * 여기서 SR을 주는 것은 약속("61장째는 SSR")을 어기는 것이다. 이미 그 등급 이상이면 강제가 아니다.
 */
export function forcedGrade(
  rolled: Grade,
  pity: Pity,
  table: EnvelopeTable = ENVELOPE_TABLE,
): Readonly<{ grade: Grade; forcedBy: PityForce }> {
  if (pity.sinceLegend >= table.pity.legendAfter && rolled !== 'legend') {
    return { grade: 'legend', forcedBy: 'legend' }
  }
  if (pity.sinceRare >= table.pity.rareAfter && gradeRank(rolled) < gradeRank('rare')) {
    return { grade: 'rare', forcedBy: 'rare' }
  }
  return { grade: rolled, forcedBy: null }
}

/**
 * 피티 게이지가 말하는 남은 장수. **그 장째가 보장된다**는 뜻이다 - 카운터 7, 임계 10이면 아래 등급으로
 * 세 장을 더 열 수 있고 네 번째가 SR 이상이다(3.2절 "SR 없이 10장 → 11장째"). 임계에 닿았으면 1이다(다음 장).
 *
 * 남은 장수를 세는 자리에 `N`을 쓰지 않는다 - P5에서 `N`이 등급 이름이 되어, 그 글자가 수를 뜻하는지
 * 가장 낮은 등급을 뜻하는지가 문장마다 갈린다.
 *
 * `forcedGrade`의 `>=` 비교에서 **역산한 값**이라 그 옆에 둔다 - 임계의 뜻(≥인가 >인가)이 바뀌면 두 함수가 함께
 * 바뀌어야 하고, 화면 층에 두면 게이지가 한 장 어긋나도 아무 테스트가 모른다.
 */
export function pityRemaining(pity: Pity, table: EnvelopeTable = ENVELOPE_TABLE): Readonly<{ rare: number; legend: number }> {
  return {
    rare: Math.max(1, table.pity.rareAfter - pity.sinceRare + 1),
    legend: Math.max(1, table.pity.legendAfter - pity.sinceLegend + 1),
  }
}

/**
 * 봉투 하나를 연 뒤의 피티. **카운터는 해당 등급 이상에서만 0으로 돌아간다** - SR이 나오면
 * `sinceRare`만 0이고 `sinceLegend`는 계속 오른다. SSR은 둘 다 0이다(SSR은 SR 이상이다).
 */
export function advancePity(pity: Pity, grade: Grade): Pity {
  return {
    sinceRare: gradeRank(grade) >= gradeRank('rare') ? 0 : pity.sinceRare + 1,
    sinceLegend: grade === 'legend' ? 0 : pity.sinceLegend + 1,
  }
}

/* --- 열기 -------------------------------------------------------------------- */

/**
 * 봉투 하나를 연다. 같은 `(accountSeed, seq, pity)`는 언제나 같은 결과다.
 *
 * `seq`가 1 이상의 정수가 아니면 던진다. 이것은 호출자의 버그다 - `EnvelopeState.seq`에서 파생되는 값이고
 * `?pull=`은 파서가 먼저 거른다. 조용히 1로 접으면 "3회차를 재현했는데 1회차가 나왔다"가 된다.
 *
 * @throws {RangeError} `seq`가 1 이상의 안전한 정수가 아닐 때
 */
export function openEnvelope(
  accountSeed: string,
  seq: number,
  pity: Pity,
  table: EnvelopeTable = ENVELOPE_TABLE,
): DrawResult {
  if (!Number.isSafeInteger(seq) || seq < FIRST_SEQ) {
    throw new RangeError(`openEnvelope: seq는 ${String(FIRST_SEQ)} 이상의 정수여야 한다 (받은 값: ${String(seq)})`)
  }

  const rng = createRng(envelopeSeed(accountSeed, seq))
  // 두 롤을 **먼저, 무조건** 뽑는다. 아래 어느 분기도 난수를 더 쓰지 않는다(머리말 "난수 소비량은 언제나 둘").
  const gradeRoll = rng.next()
  const itemRoll = rng.next()

  const rolledGrade = gradeForRoll(gradeRoll, table)
  const { grade, forcedBy } = forcedGrade(rolledGrade, pity, table)

  const pool = table.pools[grade]
  // `validateEnvelopeTable`이 빈 풀을 막지만, 임의의 표를 받는 이상 여기서도 말한다.
  if (pool.length === 0) throw new RangeError(`openEnvelope: '${grade}' 풀이 비어 있다`)
  const reward = pickByWeight(pool, itemRoll, grade)

  return {
    seq,
    grade,
    reward,
    rolledGrade,
    gradeRoll,
    itemRoll,
    forcedBy,
    pityAfter: advancePity(pity, grade),
  }
}

/* --- 계정 상태 ------------------------------------------------------------------ */

/** 아직 한 장도 열지 않은 계정. */
export function createEnvelopeState(): EnvelopeState {
  return { seq: 0, ...FRESH_PITY, log: [] }
}

export function pityOf(state: EnvelopeState): Pity {
  return { sinceRare: state.sinceRare, sinceLegend: state.sinceLegend }
}

/** 다음 봉투의 번호. 이력이 잘려도 `seq`는 따로 저장되므로 되돌아가지 않는다. */
export function nextSeq(state: EnvelopeState): number {
  return state.seq + 1
}

/** 오래된 이력부터 버린다. 최근 것이 화면(사물함의 "최근 봉투")에 쓰이므로 뒤를 남긴다. */
function trimLog(log: readonly DrawResult[]): readonly DrawResult[] {
  return log.length <= ENVELOPE_LOG_LIMIT ? log : log.slice(log.length - ENVELOPE_LOG_LIMIT)
}

/**
 * 다음 봉투를 열고 상태를 한 칸 옮긴다. 핀 결제·산출물 적용은 여기 없다 - 그 둘은 `pins.ts`·`inventory.ts`가
 * 각자 하고 `main.ts`가 한 덩어리로 저장한다. 이 함수는 "몇 번째를 열었고 피티가 어떻게 됐나"만 안다.
 */
export function drawNext(
  accountSeed: string,
  state: EnvelopeState,
  table: EnvelopeTable = ENVELOPE_TABLE,
): Readonly<{ state: EnvelopeState; result: DrawResult }> {
  const result = openEnvelope(accountSeed, nextSeq(state), pityOf(state), table)
  return {
    result,
    state: {
      seq: result.seq,
      sinceRare: result.pityAfter.sinceRare,
      sinceLegend: result.pityAfter.sinceLegend,
      log: trimLog([...state.log, result]),
    },
  }
}

/**
 * `seq`회차를 열기 **직전**의 피티. 첫 봉투는 새 계정 값이고, 그 뒤는 바로 앞 회차의 `pityAfter`다.
 * 앞 회차가 이력(`ENVELOPE_LOG_LIMIT`) 밖으로 잘렸으면 `null` - 모르는 값을 0으로 메우면 게이지가 "SR까지 11장"이라
 * 거짓말을 하고, 같은 화면의 카드는 이력의 진짜 결과(피티 강제 스탬프 포함)를 보여 서로 다른 말을 한다.
 * 화면은 `null`이면 게이지를 감춘다. `accountEconomy.replay`와 `replayDraw`가 같은 함수를 부른다.
 */
export function pityBeforeSeq(state: EnvelopeState, seq: number): Pity | null {
  if (seq === FIRST_SEQ) return FRESH_PITY
  return state.log.find((entry) => entry.seq === seq - 1)?.pityAfter ?? null
}

/**
 * 특정 회차를 다시 계산한다(`?pull=seq`). 저장을 건드리지 않는다.
 *
 * **이력(최근 20회차) 안의 회차만 그대로다.** 이력 밖이면 그 회차의 피티를 모르므로 새 계정 피티로 계산하고,
 * 그래서 `grade`·`forcedBy`가 원래 회차와 다를 수 있다. 바늘 값(`gradeRoll`)은 시드에서만 오므로 언제나 같다 -
 * 연출 재현 도구로는 그것으로 충분하고, 이 한계는 `urlState.ts`의 `?pull=` 줄에 적혀 있다.
 */
/**
 * 무게 비율로 한 줄을 고른다. **롤 하나만 쓴다** - 누적 합을 걷는 것이지 다시 뽑는 것이 아니다
 * (`openEnvelope` 머리말의 "난수 소비량은 언제나 둘"이 이 함수의 계약이다).
 *
 * 마지막 줄을 미리 앉혀 두는 이유는 `itemRoll`이 정확히 1일 때다 - 누적이 총합에 닿아 어느 줄에서도
 * 멈추지 않는다. 부동소수점 누적 오차로 마지막 한 줄이 아주 드물게 빠지는 것도 같은 자리에서 막힌다.
 */
function pickByWeight(pool: readonly PoolEntry[], itemRoll: number, grade: Grade): Reward {
  let total = 0
  for (const entry of pool) total += entry.weight
  if (total <= 0) throw new RangeError(`openEnvelope: '${grade}' 풀의 무게 합이 0이다`)

  const last = pool[pool.length - 1]
  if (last === undefined) throw new RangeError(`openEnvelope: '${grade}' 풀에 구멍이 있다`)

  let cursor = itemRoll * total
  for (const entry of pool) {
    cursor -= entry.weight
    if (cursor < 0) return entry.reward
  }
  return last.reward
}

export function replayDraw(
  accountSeed: string,
  state: EnvelopeState,
  seq: number,
  table: EnvelopeTable = ENVELOPE_TABLE,
): DrawResult {
  const logged = state.log.find((entry) => entry.seq === seq)
  if (logged !== undefined) return logged
  return openEnvelope(accountSeed, seq, pityBeforeSeq(state, seq) ?? FRESH_PITY, table)
}

/* --- 저장값 읽기 -------------------------------------------------------------- */

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function nonNegativeInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null
}

function unitInterval(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value < 1 ? value : null
}

/** 저장된 결과 한 줄. 모양이 아니면 `null` - 그 줄만 버리고 나머지는 살린다. */
export function parseDrawResult(value: unknown): DrawResult | null {
  if (!isRecordLike(value)) return null

  const seq = nonNegativeInteger(value['seq'])
  const grade = value['grade']
  const rolledGrade = value['rolledGrade']
  const reward = parseReward(value['reward'])
  const gradeRoll = unitInterval(value['gradeRoll'])
  const itemRoll = unitInterval(value['itemRoll'])
  const forcedBy = value['forcedBy']
  const pityAfter = value['pityAfter']

  if (seq === null || seq < FIRST_SEQ || !isGrade(grade) || reward === null) return null
  if (gradeRoll === null || itemRoll === null) return null
  if (forcedBy !== null && forcedBy !== 'rare' && forcedBy !== 'legend') return null
  if (!isRecordLike(pityAfter)) return null
  const sinceRare = nonNegativeInteger(pityAfter['sinceRare'])
  const sinceLegend = nonNegativeInteger(pityAfter['sinceLegend'])
  if (sinceRare === null || sinceLegend === null) return null

  return {
    seq,
    grade,
    reward,
    // 옛 이력에 없을 수 있는 필드다. 없으면 강제가 없었던 것으로 본다(등급과 같다).
    rolledGrade: isGrade(rolledGrade) ? rolledGrade : grade,
    gradeRoll,
    itemRoll,
    forcedBy,
    pityAfter: { sinceRare, sinceLegend },
  }
}

/**
 * 저장값 → 봉투 상태. **어떤 입력에도 던지지 않는다.** 읽을 수 없으면 새 계정 상태다.
 *
 * 피티 카운터가 깨졌을 때 0으로 떨어지는 것은 손해의 방향이 맞다 - 59장 쌓은 게이지가 사라지는 것은
 * 아쉽지만, 반대로 거대한 수가 들어와 즉시 SSR이 강제되면 그건 저장 조작 경로가 된다.
 */
export function parseEnvelopeState(value: unknown): EnvelopeState {
  if (!isRecordLike(value)) return createEnvelopeState()

  const rawLog = value['log']
  const log: DrawResult[] = []
  if (Array.isArray(rawLog)) {
    for (const entry of rawLog) {
      const parsed = parseDrawResult(entry)
      if (parsed !== null) log.push(parsed)
    }
  }

  return {
    seq: nonNegativeInteger(value['seq']) ?? 0,
    sinceRare: nonNegativeInteger(value['sinceRare']) ?? 0,
    sinceLegend: nonNegativeInteger(value['sinceLegend']) ?? 0,
    log: trimLog(log),
  }
}
